import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import axios from '../../../sharedModules/utils/axios.utils';

interface ChillerLogFieldSettingsDataTableProps {
  role: string;
}

const ChillerLogFieldSettingsDataTable: React.FC<ChillerLogFieldSettingsDataTableProps> = ({ role }) => {
  const [allFields, setAllFields] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingField, setEditingField] = useState<any>(null);

  const fetchFieldLabels = async () => {
    try {
      const response: any = await axios.get('/chiller_logs/log_fields/new');
      const fields = response.data.all_fields.map((field: any, index: number) => ({ ...field, _index: index }));
      setAllFields(fields);
    } catch (error) {
      console.error('Error fetching field labels:', error);
    }
  };

  useEffect(() => {
    fetchFieldLabels();
  }, []);

  const handleSubmit = async () => {
    try {
      await axios.post('/chiller_logs/log_fields', allFields);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleEditClick = (row: any) => {
    setEditingField({ ...row });
    setModalOpen(true);
  };

  const handleModalSave = () => {
    setAllFields((prevFields) =>
      prevFields.map((field) => (field._index === editingField._index ? editingField : field)),
    );
    setModalOpen(false);
    setEditingField(null);
  };

  const handleModalChange = (fieldName: string, value: any) => {
    setEditingField((prev: any) => ({ ...prev, [fieldName]: value }));
  };

  const columns = [
    { name: 'Section', selector: (row: any) => row.section, sortable: true },
    { name: 'Display Name', selector: (row: any) => row.display_name, sortable: true },
    { name: 'Log Type', selector: (row: any) => row.cooler_type, sortable: true },
    { name: 'Min', selector: (row: any) => row.min_value, sortable: true },
    { name: 'Max', selector: (row: any) => row.max_value, sortable: true },
    { name: 'Servman Name', selector: (row: any) => row.db_name, sortable: true },
    {
      name: 'Actions',
      cell: (row: any) => (
        <Button variant="contained" color="primary" onClick={() => handleEditClick(row)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h4">Chiller Log Fields</Typography>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <DataTable
              title=""
              columns={columns}
              data={allFields}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              dense
              highlightOnHover
              pointerOnHover
              noHeader
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Save
        </Button>
      </Grid>
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Edit Field</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ marginTop: 20 }}>
              <TextField
                label="Display Name"
                value={editingField?.display_name || ''}
                onChange={(e) => handleModalChange('display_name', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Min"
                type="number"
                value={editingField?.min_value || 0}
                onChange={(e) => handleModalChange('min_value', parseFloat(e.target.value))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max"
                type="number"
                value={editingField?.max_value || 0}
                onChange={(e) => handleModalChange('max_value', parseFloat(e.target.value))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Servman Name"
                value={editingField?.db_name || ''}
                disabled
                onChange={(e) => null}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleModalSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ChillerLogFieldSettingsDataTable;
